import { observer } from "mobx-react-lite";
import { marked, Marked } from "marked";
import DOMPurify from "dompurify";

// One dedicated Marked instance per heading offset, so the global `marked`
// instance stays untouched for every other caller.
const shiftedMarkedInstances = new Map<number, Marked>();

function markedFor(headingOffset: number) {
  if (headingOffset <= 0) return marked;
  let instance = shiftedMarkedInstances.get(headingOffset);
  if (!instance) {
    instance = new Marked({
      walkTokens(token) {
        // Only Markdown headings (`#`, setext); raw HTML `<h1>` is left alone.
        if (token.type === "heading") {
          token.depth = Math.min(6, token.depth + headingOffset);
        }
      },
    });
    shiftedMarkedInstances.set(headingOffset, instance);
  }
  return instance;
}

export const MarkdownText = observer(function MarkdownText({
  text,
  extraClasses = "",
  headingOffset = 0,
}: {
  text: string;
  extraClasses?: string;
  /** Shift Markdown heading levels down by this amount (clamped to h6). */
  headingOffset?: number;
}) {
  if (!text) return null;
  const rawHtml = (markedFor(headingOffset).parse(text) as string).replaceAll(
    /<a(?! href="(https:\/\/www\.mapmf\.org\/|http:\/\/localhost))/g,
    "<a target='_blank' rel='noopener noreferrer'",
  );
  return (
    <div
      class={`prose max-w-full dark:prose-invert! ${extraClasses}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(rawHtml, { ADD_ATTR: ["target"] }),
      }}
    />
  );
});
